import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
  Button,
  Alert,
  Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
//import api from "../../api/axios"; // adapte à ton projet :contentReference[oaicite:2]{index=2}
import { pageWrap, tableHeaderCell, tableRow, btnPrimary, minutesFmt } from "../../utils/pageStyles";
import { AdminSettingsAPI } from "../../api/adminSettingsApi";
const DEFAULTS = {
  credit_debit_mode: "reserved", // reserved|consumed
  allow_pack_stacking: true,
};

export default function AdminCreditsSettingsPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState(DEFAULTS.credit_debit_mode);
  const [stacking, setStacking] = useState(DEFAULTS.allow_pack_stacking);

  const dirty = useMemo(() => {
    // si tu veux du “dirty tracking” plus strict, stocke la valeur initiale après load()
    return true;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fix endpoint (ancien: "/admin/settings/credits") :contentReference[oaicite:3]{index=3}
      /*const res = await api.get("/api/v1/admin/settings/credits");
      setMode(res.data?.credit_debit_mode || "reserved");
      setStacking(!!res.data?.allow_pack_stacking);*/
      const data = await AdminSettingsAPI.getCreditsSettings();
      setMode(data?.credit_debit_mode || "reserved");
      setStacking(!!data?.allow_pack_stacking);

    } catch (e) {
     // enqueueSnackbar(t("snackbar.error"), { variant: "error" });
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await AdminSettingsAPI.updateCreditsSettings({
        credit_debit_mode: mode,
        allow_pack_stacking: stacking,
      });
      enqueueSnackbar(t("admin.creditsSettings.snack.saved"), { variant: "success" });
      await load();
    } catch (e) {
      //enqueueSnackbar(t("snackbar.error"), { variant: "error" });
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  }, [enqueueSnackbar, load, mode, stacking, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={pageWrap}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" gap={1} alignItems="center">
          <SettingsIcon />
          <Box>
            <Typography variant="h5">{t("admin.creditsSettings.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("admin.creditsSettings.subtitle")}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading || saving}
          >
            {t("common.refresh")}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={save}
            disabled={loading || saving}
          >
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2} sx={{ maxWidth: 900 }}>
        {loading && (
          <Card>
            <CardContent>
              <Stack direction="row" gap={2} alignItems="center">
                <CircularProgress size={22} />
                <Typography>{t("lazy.loadingData")}</Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {!loading && (
          <>
            <Card>
              <CardHeader
                title={t("admin.creditsSettings.sections.debitMode.title")}
                subheader={t("admin.creditsSettings.sections.debitMode.help")}
              />
              <CardContent>
                <FormControl fullWidth>
                  <InputLabel id="debit-mode-label">
                    {t("admin.creditsSettings.fields.debitMode")}
                  </InputLabel>
                  <Select
                    labelId="debit-mode-label"
                    label={t("admin.creditsSettings.fields.debitMode")}
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    <MenuItem value="reserved">
                      {t("admin.creditsSettings.debitMode.reserved")}
                    </MenuItem>
                    <MenuItem value="consumed">
                      {t("admin.creditsSettings.debitMode.consumed")}
                    </MenuItem>
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={t("admin.creditsSettings.hints.currentMode", { mode })}
                    variant="outlined"
                  />
                  <Chip
                    label={t("admin.creditsSettings.hints.defaultIsReserved")}
                    variant="outlined"
                  />
                </Stack>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {t("admin.creditsSettings.debitMode.note")}
                  </Typography>
                </Alert>
              </CardContent>
            </Card>

            <Card>
              <CardHeader
                title={t("admin.creditsSettings.sections.stacking.title")}
                subheader={t("admin.creditsSettings.sections.stacking.help")}
              />
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={stacking}
                      onChange={(e) => setStacking(e.target.checked)}
                    />
                  }
                  label={t("admin.creditsSettings.fields.allowStacking")}
                />

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {t("admin.creditsSettings.stacking.recommendation")}
                  </Typography>
                </Alert>
              </CardContent>
            </Card>

            <Typography variant="caption" color="text.secondary">
              {dirty ? t("admin.creditsSettings.hints.dirty") : ""}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}
